import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invitationService } from '../../services/invitationService';
import { useAuth } from '../../contexts/AuthContext';
import type { InvitationDetails, AgentRegistrationRequest } from '../../types/auth';

export const AgentInvitationSetup: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { setAuthFromResponse } = useAuth();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [activityInterests, setActivityInterests] = useState<string[]>([]);

  // Available activity options
  const activityOptions = [
    'Tennis', 'Basketball', 'Soccer', 'Volleyball', 'Badminton', 
    'Squash', 'Table Tennis', 'Swimming', 'Fitness', 'Other'
  ];

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setIsLoading(false);
      return;
    }

    loadInvitationDetails();
  }, [token]);

  const loadInvitationDetails = async () => {
    try {
      setIsLoading(true);
      const details = await invitationService.getInvitationDetails(token!);
      setInvitation(details);

      if (details.isExpired === true) {
        setError('This invitation has expired');
      } else if (details.isUsed === true) {
        setError('This invitation has already been used');
      }
    } catch (err) {
      console.error('Failed to load invitation:', err);
      setError('Invalid or expired invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!firstName.trim()) errors.push('First name is required');
    if (!lastName.trim()) errors.push('Last name is required');
    if (!phone.trim()) errors.push('Phone number is required');
    if (!dateOfBirth) errors.push('Date of birth is required');
    if (!password) errors.push('Password is required');
    if (password.length < 8) errors.push('Password must be at least 8 characters');
    if (password !== confirmPassword) errors.push('Passwords do not match');

    return errors;
  };

  const handleActivityToggle = (activity: string) => {
    setActivityInterests(prev =>
      prev.includes(activity)
        ? prev.filter(a => a !== activity)
        : [...prev, activity]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    if (!invitation || invitation.isExpired === true || invitation.isUsed === true) {
      setError('Invalid invitation');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const registrationData: AgentRegistrationRequest = {
        invitationToken: token!,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        phone: phone.trim(),
        dateOfBirth,
        activityInterests,
      };

      const authResponse = await invitationService.registerAgent(registrationData);

      // SW-95: hydrate AuthContext's live state, not just localStorage —
      // otherwise ProtectedRoute still sees isAuthenticated=false right
      // after this navigate (no full page reload happens), so the agent
      // hit the login screen instead of landing in the portal.
      setAuthFromResponse(authResponse);

      // Navigate to main app
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#23262b] flex items-center justify-center">
        <div className="text-[#9aa3b2]">Loading invitation details...</div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-[#23262b] flex items-center justify-center">
        <div className="bg-[#2c3036] rounded-lg border border-white/[0.08] p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-[#e1a08a] text-lg font-semibold mb-4">Invalid Invitation</div>
            <p className="text-[#9aa3b2] mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-[#4d63ac] text-white rounded-lg hover:bg-[#5b71be] transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#23262b] flex items-center justify-center p-4">
      <div className="bg-[#2c3036] rounded-lg border border-white/[0.08] p-8 max-w-2xl w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Complete Your Registration</h1>
          <p className="text-[#9aa3b2]">
            You've been invited to join <span className="text-white font-semibold">{invitation?.businessName}</span>
          </p>
          <p className="text-[#9aa3b2] text-sm mt-1">
            Email: {invitation?.email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                First Name *
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent"
                placeholder="Enter your first name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                Last Name *
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent"
                placeholder="Enter your last name"
                required
              />
            </div>
          </div>

          {/* Contact fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent"
                placeholder="+1234567890"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                Date of Birth *
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Password fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                Password *
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent"
                placeholder="Enter password"
                minLength={8}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                Confirm Password *
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent"
                placeholder="Confirm password"
                required
              />
            </div>
          </div>

          {/* Activity interests */}
          <div>
            <label className="block text-sm font-medium text-[#c5cde0] mb-3">
              Activity Interests (Optional)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {activityOptions.map((activity) => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => handleActivityToggle(activity)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activityInterests.includes(activity)
                      ? 'bg-[#4d63ac] text-white'
                      : 'bg-[#2a2e34] text-[#c5cde0] hover:bg-[#34383f]'
                  }`}
                >
                  {activity}
                </button>
              ))}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-[#c06f57]/15 border border-[#c06f57]/30 rounded-lg p-4">
              <p className="text-[#e1a08a]">{error}</p>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting || Boolean(invitation && (invitation.isExpired === true || invitation.isUsed === true))}
            className="w-full bg-[#4d63ac] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#5b71be] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Creating Account...' : 'Complete Registration'}
          </button>
        </form>
      </div>
    </div>
  );
};